import { useState, useMemo, useRef, useCallback, useEffect, DragEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import { message, Modal, Select } from "antd";
import {
	CheckCircle2,
	Circle,
	Camera,
	Video,
	AlertTriangle,
	ChevronLeft,
	ChevronRight,
	Sparkles,
	Upload as UploadIcon,
	Check,
	Loader2,
	RotateCcw,
	X,
	Eye,
	ShieldCheck,
	AlertCircle,
	Info,
	MapPin,
	Building2,
	FileText
} from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import Icon, { ICON_SOURCES, IconSource } from "components/icons/icon";
import MediaPlayer from "components/media-player/media-player";

import routeNames from "constants/route-names";
import {
	InspectionChecklistItemStatus,
	InspectionEvidenceReviewStatus,
	InspectionJobStatus,
	InspectionProvisionalVerdictStatus,
	MediaType
} from "constants/field-values";

import { useFileUpload } from "hooks/useFileUpload";
import { useHighPrecisionGpsLocation } from "hooks/useHighPrecisionLocation";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { getFileIcon, deleteFromStorage } from "utils/files-management";
import { getInspectionTypeLabel, getPropertyAddress } from "utils/helpers";
import {
	evaluateGpsLocationAgainstGeofence,
	buildLocationVerification,
	DEFAULT_GEOFENCE_RADIUS_METERS,
	GpsReading,
	LocationEvaluation,
	LocationVerification
} from "utils/geolocation";

import {
	useGetInspectionJobSubscription,
	GetInspectionJobSubscription
} from "graphql/subscriptions/inspection-job-by-id.generated";
import {
	useSubmitEvidenceSubmissionsMutation,
	SubmitEvidenceSubmissionsMutation
} from "graphql/mutations/submit-evidence-submissions.generated";
import {
	useResetInspectionChecklistItemEvidenceMutation,
	ResetInspectionChecklistItemEvidenceMutation
} from "graphql/mutations/reset-inspection-checklist-item-evidence.generated";
import { useUpdateInspectionJobStateMutation } from "graphql/mutations/update-inspection-job-state.generated";
import { useGetPropertyLocationHierarchyQuery } from "graphql/queries/get-property-location-hierarchy.generated";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildInspectionJobDescription } from "utils/ollie-screen-description";

const itemStatusConfig = {
	[InspectionChecklistItemStatus.PENDING]: {
		icon: Circle,
		colorClass: "iec-status-color-pending",
		bgClass: "iec-status-bg-pending",
		borderClass: "iec-main-card-border-pending"
	},
	[InspectionChecklistItemStatus.UPLOADING]: {
		icon: UploadIcon,
		colorClass: "iec-status-color-uploading",
		bgClass: "iec-status-bg-uploading",
		borderClass: "iec-main-card-border-uploading"
	},
	[InspectionChecklistItemStatus.ANALYZING]: {
		icon: Sparkles,
		colorClass: "iec-status-color-analyzing",
		bgClass: "iec-status-bg-analyzing",
		borderClass: "iec-main-card-border-analyzing"
	},
	[InspectionChecklistItemStatus.READY]: {
		icon: CheckCircle2,
		colorClass: "iec-status-color-ready",
		bgClass: "iec-status-bg-ready",
		borderClass: "iec-main-card-border-ready"
	},
	[InspectionChecklistItemStatus.WARNING]: {
		icon: AlertTriangle,
		colorClass: "iec-status-color-warning",
		bgClass: "iec-status-bg-warning",
		borderClass: "iec-main-card-border-warning"
	},
	[InspectionChecklistItemStatus.FAIL]: {
		icon: AlertTriangle,
		colorClass: "iec-status-color-fail",
		bgClass: "iec-status-bg-fail",
		borderClass: "iec-main-card-border-fail"
	}
} as const;

const CAT_CIRCLE_R = 14;
const CAT_CIRCLE_C = 2 * Math.PI * CAT_CIRCLE_R; // ≈ 87.96

type ChecklistItem = NonNullable<
	GetInspectionJobSubscription["inspection_jobs_by_pk"]
>["inspection_checklist_items"][number];

type ItemType = "photo" | "video" | "document";

interface StagedFile {
	uid: string;
	name: string;
	file: File;
	type: string; // derived source type (e.g. "photo", "video", "document")
	mimeType: string;
	localUrl: string;
}

// Paperwork rather than an on-site capture: uploaded from anywhere, so it carries no GPS proof.
const REQUIRED_DOCUMENTS_CATEGORY = "required_documents";

const isRequiredDocumentsItem = (item: ChecklistItem): boolean => item.category === REQUIRED_DOCUMENTS_CATEGORY;

const getItemStatus = (item: ChecklistItem): InspectionChecklistItemStatus =>
	(item.status as InspectionChecklistItemStatus) ?? InspectionChecklistItemStatus.PENDING;

const getItemType = (item: ChecklistItem): ItemType => {
	if (isRequiredDocumentsItem(item)) return "document";
	return (item.evidence_submissions[0]?.source_type as ItemType) ?? "photo";
};

const deriveSourceType = (mimeType: string): ItemType => {
	if (mimeType.startsWith("image/")) return "photo";
	if (mimeType.startsWith("video/")) return "video";
	return "document";
};

const mimeToMediaType = (mimeType: string): MediaType => {
	if (mimeType.startsWith("image/")) return MediaType.Image;
	if (mimeType.startsWith("video/")) return MediaType.Video;
	if (mimeType.startsWith("audio/")) return MediaType.Audio;
	return MediaType.Document;
};

const formatFileSize = (bytes: number): string => {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

type VerdictTone = "pass" | "warning" | "fail";

// Maps the perception finding_status (what is physically there) to a pill in the
// Observations section of the Analysis block.
const observationPillConfig: Record<string, { label: string; tone: VerdictTone; icon: typeof CheckCircle2 }> = {
	PRESENT: { label: "Present", tone: "pass", icon: CheckCircle2 },
	OBSTRUCTED: { label: "Obstructed", tone: "warning", icon: AlertTriangle },
	DAMAGED: { label: "Damaged", tone: "fail", icon: AlertTriangle },
	MISSING: { label: "Missing", tone: "fail", icon: AlertTriangle },
	UNCERTAIN: { label: "Uncertain", tone: "warning", icon: AlertTriangle }
};

// Maps the provisional compliance verdict (does it meet the criteria) to a pill in the
// Compliance section. Always rendered as preliminary while collecting.
const compliancePillConfig: Record<string, { label: string; tone: VerdictTone; icon: typeof CheckCircle2 }> = {
	[InspectionProvisionalVerdictStatus.PASS]: { label: "Pass", tone: "pass", icon: CheckCircle2 },
	[InspectionProvisionalVerdictStatus.WARNING]: { label: "Warning", tone: "warning", icon: AlertTriangle },
	[InspectionProvisionalVerdictStatus.FAIL]: { label: "Fail", tone: "fail", icon: AlertTriangle },
	[InspectionProvisionalVerdictStatus.UNCERTAIN]: { label: "Uncertain", tone: "warning", icon: AlertTriangle }
};

// Number → coarse confidence level + how many of the 3 bars to fill.
const confidenceToLevel = (c?: number | null): { label: string; bars: number } | null => {
	if (c == null) return null;
	if (c >= 0.8) return { label: "High", bars: 3 };
	if (c >= 0.5) return { label: "Medium", bars: 2 };
	return { label: "Low", bars: 1 };
};

// "HIGH" → "High Severity"
const formatSeverity = (s?: string | null): string | null =>
	s ? `${s.charAt(0)}${s.slice(1).toLowerCase()} Severity` : null;

// applicable_codes is a jsonb array (or legacy string); render as a readable source line.
const formatApplicableCodes = (codes: unknown): string | null => {
	if (Array.isArray(codes) && codes.length > 0) return codes.join(", ");
	if (typeof codes === "string" && codes.trim().length > 0) return codes;
	return null;
};

const ConfidenceMeter = ({ value }: { value?: number | null }) => {
	const level = confidenceToLevel(value);
	if (!level) return null;
	return (
		<div className="iec-an-confidence">
			<span className="iec-an-conf-label">Confidence</span>
			<span className="iec-an-conf-bars">
				{[1, 2, 3].map(n => (
					<span key={n} className={`iec-an-conf-bar ${n <= level.bars ? "iec-an-conf-bar-on" : ""}`} />
				))}
			</span>
			<span className="iec-an-conf-level">{level.label}</span>
		</div>
	);
};

const OllieEvidenceCollection = () => {
	const navigate = useNavigate();
	const params = useParams<{ jobId: string }>();
	const { loggedUserValue } = useLoggedUser();

	const [currentIndex, setCurrentIndex] = useState(0);
	const [items, setItems] = useState<ChecklistItem[]>([]);
	const [stagedFilesByItem, setStagedFilesByItem] = useState<Record<string, StagedFile[]>>({});
	const [uploadingItemIds, setUploadingItemIds] = useState<Set<string>>(new Set());
	const [resettingItemIds, setResettingItemIds] = useState<Set<string>>(new Set());
	// Bridges the gap between mutation completion and the subscription pushing status=ANALYZING.
	// Without this, the UI briefly re-renders the dropzone (PENDING + no stagedFile) right after
	// the upload finishes and the staged file is cleared.
	const [awaitingAnalysisItemIds, setAwaitingAnalysisItemIds] = useState<Set<string>>(new Set());
	const [submittingForAnalysis, setSubmittingForAnalysis] = useState(false);
	const [fileViewer, setFileViewer] = useState<{ type: MediaType | null; src: string }>({ type: null, src: "" });

	// capture-time location verification + room selection.
	const { acquireBestGpsLocation } = useHighPrecisionGpsLocation();
	const [permissionDeniedOpen, setPermissionDeniedOpen] = useState(false);
	const [checkingLocation, setCheckingLocation] = useState(false);
	// Building/Floor/Room cascade selection (applies to the captures being submitted).
	const [selectedBuildingId, setSelectedBuildingId] = useState<string | null>(null);
	const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null);
	const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);

	// Sync access to staged files — guarantees the file list is readable without waiting for a re-render.
	const stagedFilesRef = useRef<Record<string, StagedFile[]>>({});
	// Tracks which item was active when the file picker was opened so files land on the right item.
	const activeItemForModalRef = useRef<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const currentItem = items[currentIndex];
	const stagedFiles = currentItem ? (stagedFilesByItem[currentItem.id] ?? []) : [];
	const isUploading = currentItem ? uploadingItemIds.has(currentItem.id) : false;
	const isResetting = currentItem ? resettingItemIds.has(currentItem.id) : false;
	const isAwaitingAnalysis = currentItem ? awaitingAnalysisItemIds.has(currentItem.id) : false;

	const { uploadFiles, removeFile } = useFileUpload();

	const removeStagedFile = useCallback((itemId: string, uid?: string) => {
		const existing = stagedFilesRef.current[itemId] ?? [];
		if (uid) {
			const updated = existing.filter(f => {
				if (f.uid === uid) {
					URL.revokeObjectURL(f.localUrl);
					return false;
				}
				return true;
			});
			if (updated.length === 0) {
				const { [itemId]: _, ...rest } = stagedFilesRef.current;
				stagedFilesRef.current = rest;
				setStagedFilesByItem(prev => {
					const { [itemId]: __, ...rest2 } = prev;
					return rest2;
				});
			} else {
				stagedFilesRef.current = { ...stagedFilesRef.current, [itemId]: updated };
				setStagedFilesByItem(prev => ({ ...prev, [itemId]: updated }));
			}
		} else {
			existing.forEach(f => URL.revokeObjectURL(f.localUrl));
			const { [itemId]: _, ...rest } = stagedFilesRef.current;
			stagedFilesRef.current = rest;
			setStagedFilesByItem(prev => {
				const { [itemId]: __, ...rest2 } = prev;
				return rest2;
			});
		}
	}, []);

	const handleOnCompleteSubmitEvidenceSubmissionsMutation = useCallback((data: SubmitEvidenceSubmissionsMutation) => {
		console.log("Submit Evidence Submissions Mutation Data: ", data);
		if (data.submit_evidence_submissions?.success) {
			message.success("Evidence submitted. Ollie is analyzing it now.");
		} else {
			message.error(data.submit_evidence_submissions?.error ?? "Failed to submit evidence. Please try again.");
		}
	}, []);

	const handleOnErrorSubmitEvidenceSubmissionsMutation = useCallback((error: ApolloError) => {
		console.log("Submit Evidence Submissions Error: ", error);
		message.error("Failed to submit evidence. Please try again.");
	}, []);

	const [submitEvidenceSubmissionsMutation, { loading: loadingSubmitEvidenceSubmissions }] =
		useSubmitEvidenceSubmissionsMutation({
			errorPolicy: "none",
			fetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorSubmitEvidenceSubmissionsMutation,
			onCompleted: handleOnCompleteSubmitEvidenceSubmissionsMutation
		});

	const handleOnCompleteResetInspectionChecklistItemEvidenceMutation = useCallback(
		async (data: ResetInspectionChecklistItemEvidenceMutation) => {
			console.log("Reset Inspection Checklist Item Evidence Mutation Data: ", data);

			const deletedSubmissions = data.delete_evidence_submissions?.returning ?? [];
			await Promise.all(
				deletedSubmissions
					.filter(s => !!s.file_url)
					.map(s =>
						deleteFromStorage(s.file_url).catch(error => {
							// DB rows are gone; a stale storage object is non-blocking and surfaces in logs.
							console.error("Failed to delete evidence file from storage:", s.file_url, error);
						})
					)
			);

			message.success("Evidence cleared. You can now upload a new file.");
		},
		[]
	);

	const handleOnErrorResetInspectionChecklistItemEvidenceMutation = useCallback((error: ApolloError) => {
		console.log("Reset Inspection Checklist Item Evidence Error: ", error);
		message.error("Couldn't reset this item. Please try again.");
	}, []);

	const [resetInspectionChecklistItemEvidenceMutation, { loading: loadingResetInspectionChecklistItemEvidence }] =
		useResetInspectionChecklistItemEvidenceMutation({
			errorPolicy: "none",
			fetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorResetInspectionChecklistItemEvidenceMutation,
			onCompleted: handleOnCompleteResetInspectionChecklistItemEvidenceMutation
		});

	const handleOnCompleteUpdateInspectionJobStateMutation = useCallback(() => {
		if (!params.jobId) return;
		navigate(routeNames.OLLIE_INSPECTION_DETAILS.replace(":jobId", params.jobId));
	}, [params.jobId, navigate]);

	const handleOnErrorUpdateInspectionJobStateMutation = useCallback((error: ApolloError) => {
		console.log("Update Inspection Job State Error: ", error);
		message.error("Couldn't submit for analysis. Please try again.");
		setSubmittingForAnalysis(false);
	}, []);

	const [updateInspectionJobStateMutation, { loading: loadingUpdateInspectionJobState }] =
		useUpdateInspectionJobStateMutation({
			errorPolicy: "none",
			fetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorUpdateInspectionJobStateMutation,
			onCompleted: handleOnCompleteUpdateInspectionJobStateMutation
		});

	const { data } = useGetInspectionJobSubscription({
		skip: !params.jobId,
		variables: { id: params.jobId },
		onData: ({ data }) => {
			const now = new Date();

			const formatedTime = new Intl.DateTimeFormat("sv-SE", {
				hour: "2-digit",
				minute: "2-digit",
				second: "2-digit",
				hour12: false
			}).format(now);

			console.log(formatedTime, " Get InspectionJob Subscription Data: ", data);
			if (data.data?.inspection_jobs_by_pk?.inspection_checklist_items) {
				setItems(data.data.inspection_jobs_by_pk.inspection_checklist_items);
			}
		}
	});

	const propertyId = data?.inspection_jobs_by_pk?.property_id;
	const property = data?.inspection_jobs_by_pk?.property;
	const propertyAddress = getPropertyAddress(property?.address, property?.standardized_address);
	// Tells Ollie which inspection is being collected, so "this item" has a subject.
	useDescribeScreenToOllie(
		buildInspectionJobDescription({
			jobId: data?.inspection_jobs_by_pk?.id,
			propertyName: property?.name,
			jurisdictionName:
				data?.inspection_jobs_by_pk?.jurisdiction_record?.name || data?.inspection_jobs_by_pk?.jurisdiction,
			inspectionType: data?.inspection_jobs_by_pk?.inspection_type,
			state: data?.inspection_jobs_by_pk?.state,
			checklistItemCount: data?.inspection_jobs_by_pk?.inspection_checklist_items?.length,
			propertyId: propertyId,
			jurisdictionId: data?.inspection_jobs_by_pk?.jurisdiction_id,
			organizationId: data?.inspection_jobs_by_pk?.organization_id,
			workspaceId: data?.inspection_jobs_by_pk?.workspace_id,
			createdByUserId: data?.inspection_jobs_by_pk?.created_by_user_id,
			isCollecting: true
		}),
		{ propertyId, jurisdictionId: data?.inspection_jobs_by_pk?.jurisdiction_id }
	);


	// Room hierarchy for the Building -> Floor -> Room cascade. Absent hierarchy
	// (no buildings) simply hides the selector — capture still works (GPS-only).
	const { data: hierarchyData } = useGetPropertyLocationHierarchyQuery({
		skip: !propertyId,
		variables: { propertyId }
	});
	const buildings = useMemo(() => hierarchyData?.buildings ?? [], [hierarchyData]);
	const hasRoomHierarchy = buildings.length > 0;

	const selectedBuilding = buildings.find(b => b.id === selectedBuildingId) ?? null;
	const floorsForBuilding = selectedBuilding?.floors ?? [];
	const selectedFloor = floorsForBuilding.find(f => f.id === selectedFloorId) ?? null;
	const roomsForFloor = selectedFloor?.rooms ?? [];

	const groupedItems: Map<string, ChecklistItem[]> = useMemo(() => {
		const grouped = new Map<string, ChecklistItem[]>();

		items.forEach(item => {
			const key = item.category ?? "Uncategorized";
			if (!grouped.has(key)) {
				grouped.set(key, []);
			}
			grouped.get(key)!.push(item);
		});

		return grouped;
	}, [items]);

	const progress = useMemo(() => {
		if (!items?.length) return 0;
		const completed = items.filter(item => item.evidence_findings.length > 0).length;
		return (completed / items.length) * 100;
	}, [items]);

	// Required items must have a completed analysis (READY/WARNING/FAIL) before the user can
	// submit the job for gap analysis. PENDING/UPLOADING/ANALYZING means evidence is still
	// being processed and the gap analysis would run against incomplete findings.
	const allRequiredItemsAnalyzed = useMemo(() => {
		const requiredItems = items.filter(item => item.required);
		if (requiredItems.length === 0) return false;
		const terminalStatuses: InspectionChecklistItemStatus[] = [
			InspectionChecklistItemStatus.READY,
			InspectionChecklistItemStatus.WARNING,
			InspectionChecklistItemStatus.FAIL
		];
		return requiredItems.every(item =>
			terminalStatuses.includes((item.status as InspectionChecklistItemStatus) ?? InspectionChecklistItemStatus.PENDING)
		);
	}, [items]);

	const jobState = data?.inspection_jobs_by_pk?.state as InspectionJobStatus | undefined;
	const canSubmitForAnalysis =
		allRequiredItemsAnalyzed &&
		jobState === InspectionJobStatus.COLLECTING &&
		uploadingItemIds.size === 0 &&
		awaitingAnalysisItemIds.size === 0 &&
		resettingItemIds.size === 0;

	const stageFile = useCallback((itemId: string, raw: { uid: string; name: string; file: File; type: string }) => {
		const file = raw.file;
		const next: StagedFile = {
			uid: raw.uid,
			name: raw.name || file.name.replace(/\.[^/.]+$/, ""),
			file,
			type: raw.type,
			mimeType: file.type,
			localUrl: URL.createObjectURL(file)
		};

		const existing = stagedFilesRef.current[itemId] ?? [];
		stagedFilesRef.current = { ...stagedFilesRef.current, [itemId]: [...existing, next] };
		setStagedFilesByItem(prev => ({ ...prev, [itemId]: [...(prev[itemId] ?? []), next] }));
	}, []);

	const handleOpenFilePicker = useCallback(() => {
		if (!currentItem) return;
		activeItemForModalRef.current = currentItem.id;
		fileInputRef.current?.click();
	}, [currentItem]);

	const handleFileInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const targetItemId = activeItemForModalRef.current;
			if (!targetItemId) return;
			const files = e.target.files;
			if (!files || files.length === 0) return;

			Array.from(files).forEach(file => {
				stageFile(targetItemId, {
					uid: `${Date.now()}-${Math.random()}`,
					name: file.name.replace(/\.[^/.]+$/, ""),
					file,
					type: deriveSourceType(file.type)
				});
			});

			e.target.value = "";
		},
		[stageFile]
	);

	const handleDragOver = useCallback((e: DragEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
	}, []);

	const handleDrop = useCallback(
		(e: DragEvent<HTMLButtonElement>) => {
			e.preventDefault();
			e.stopPropagation();
			if (!currentItem) return;
			const files = e.dataTransfer.files;
			if (!files || files.length === 0) return;

			Array.from(files).forEach(file => {
				stageFile(currentItem.id, {
					uid: `${Date.now()}-${Math.random()}`,
					name: file.name.replace(/\.[^/.]+$/, ""),
					file,
					type: deriveSourceType(file.type)
				});
			});
		},
		[currentItem, stageFile]
	);

	const handleOpenPreview = useCallback((file: StagedFile) => {
		setFileViewer({ type: mimeToMediaType(file.mimeType), src: file.localUrl });
	}, []);

	const handleOpenEvidencePreview = useCallback(evidenceSubmission => {
		if (!evidenceSubmission) return;
		const { file_url, mime_type } = evidenceSubmission;
		setFileViewer({ type: mimeToMediaType(mime_type), src: file_url });
	}, []);

	const handleClosePreview = useCallback(() => {
		setFileViewer({ type: null, src: "" });
	}, []);

	// Show the exception acknowledgment prompt (weak signal / out of range). Resolves
	// true if the user chooses "Capture Anyway", false on cancel.
	const confirmLocationException = useCallback((evaluation: LocationEvaluation): Promise<boolean> => {
		const detail =
			evaluation.reason === "out_of_range"
				? `You appear to be about ${evaluation.distanceMeters}m from the property (allowed: ${evaluation.radiusMeters}m).`
				: "Your GPS signal may be weak or inaccurate.";
		return new Promise<boolean>(resolve => {
			Modal.confirm({
				title: "Location couldn't be verified",
				content: `${detail} If you're at the property, you can still capture evidence — it will be flagged as location-unverified on this submission.`,
				okText: "Capture Anyway",
				cancelText: "Cancel",
				onOk: () => resolve(true),
				onCancel: () => resolve(false)
			});
		});
	}, []);

	// capture-time location verification. Returns the location_verification
	// payload to attach to the submissions, or null to abort the capture.
	const runLocationCheck = useCallback(async (): Promise<{
		proceed: boolean;
		verification: LocationVerification | null;
	}> => {
		const geofence = {
			latitude: property?.latitude ?? null,
			longitude: property?.longitude ?? null,
			geofenceRadiusMeters: property?.geofence_radius_meters ?? DEFAULT_GEOFENCE_RADIUS_METERS
		};

		setCheckingLocation(true);
		try {
			const result = await acquireBestGpsLocation();
			console.log("[GPS Testing] handlePreCheckLocation result:", result);

			// Permission denied is a distinct, blocking state — no acknowledgment prompt.
			if (result.status === "permission_denied") {
				console.log("[GPS Testing] Permission denied by user.");
				setPermissionDeniedOpen(true);
				return { proceed: false, verification: null };
			}

			if (result.status === "ok" && result.reading) {
				const evaluation = evaluateGpsLocationAgainstGeofence(result.reading, geofence);
				console.log("[GPS Testing] Location evaluation:", evaluation);
				if (evaluation.state === "verified") {
					return { proceed: true, verification: buildLocationVerification({ reading: result.reading, evaluation }) };
				}
				const acknowledged = await confirmLocationException(evaluation);
				if (!acknowledged) return { proceed: false, verification: null };
				return {
					proceed: true,
					verification: buildLocationVerification({
						reading: result.reading,
						evaluation,
						acknowledgedBy: loggedUserValue?.id
					})
				};
			}

			// unavailable / timeout — cannot confirm on-site. Treat as a weak-signal
			// exception (never silently block a valid capture).
			const evaluation: LocationEvaluation = {
				state: "low_accuracy",
				reason: "low_accuracy",
				distanceMeters: null,
				radiusMeters: geofence.geofenceRadiusMeters
			};
			console.log("[GPS Testing] Fallback low_accuracy evaluation (timeout/unavailable):", evaluation);
			const acknowledged = await confirmLocationException(evaluation);
			if (!acknowledged) return { proceed: false, verification: null };
			return {
				proceed: true,
				verification: buildLocationVerification({ reading: null, evaluation, acknowledgedBy: loggedUserValue?.id })
			};
		} finally {
			setCheckingLocation(false);
		}
	}, [
		property?.latitude,
		property?.longitude,
		property?.geofence_radius_meters,
		acquireBestGpsLocation,
		confirmLocationException,
		loggedUserValue?.id
	]);

	const handleSubmitEvidence = useCallback(async () => {
		if (!currentItem || !stagedFiles.length || !params.jobId) return;
		const itemId = currentItem.id;
		const filesToSubmit = [...stagedFiles];

		// Aborts cleanly (no upload) if the user cancels the exception prompt or denies permission.
		let locationVerification: LocationVerification | null = null;
		if (!isRequiredDocumentsItem(currentItem)) {
			const locationCheck = await runLocationCheck();
			if (!locationCheck.proceed) return;
			locationVerification = locationCheck.verification;
		}

		setUploadingItemIds(prev => {
			const next = new Set(prev);
			next.add(itemId);
			return next;
		});

		try {
			// Upload every staged file to Storage in parallel.
			const uploadResults = await uploadFiles(filesToSubmit.map(s => s.file));

			const submissions = filesToSubmit
				.map((staged, idx) => {
					const result = uploadResults[idx];
					if (!result?.publicUrl) return null;
					if (result.id) removeFile(result.id);
					return {
						uploaded_by_user_id: loggedUserValue?.id,
						source_type: deriveSourceType(staged.mimeType),
						file_name: staged.name,
						file_url: result.publicUrl,
						file_size_bytes: staged.file.size,
						mime_type: staged.mimeType,
						review_status: InspectionEvidenceReviewStatus.PENDING,
						// Only what the user picked; the backend derives the missing ancestors.
						location_verification: locationVerification,
						property_id: propertyId,
						building_id: selectedBuildingId,
						floor_id: selectedFloorId,
						room_id: selectedRoomId
						// EXIF (gps lat/lon, timestamp) is intentionally null. Adding a reliable client-side
						// EXIF parser requires a dedicated library; the backend can populate these from file_url.
					};
				})
				.filter((s): s is NonNullable<typeof s> => s !== null);

			if (submissions.length === 0) return;

			// One action call covers every file — atomic checklist item status, one unified finding.
			await submitEvidenceSubmissionsMutation({
				variables: {
					input: {
						job_id: params.jobId,
						checklist_item_id: itemId,
						submissions
					}
				}
			});

			removeStagedFile(itemId);

			// Hold "analyzing" optimistically until the subscription pushes the new submission
			// rows + status. Cleared in the effect below.
			setAwaitingAnalysisItemIds(prev => {
				const next = new Set(prev);
				next.add(itemId);
				return next;
			});
		} catch (error) {
			// Error already surfaced via onError handler.
		} finally {
			setUploadingItemIds(prev => {
				const next = new Set(prev);
				next.delete(itemId);
				return next;
			});
		}
	}, [
		currentItem,
		stagedFiles,
		params.jobId,
		loggedUserValue?.id,
		uploadFiles,
		submitEvidenceSubmissionsMutation,
		removeFile,
		removeStagedFile,
		runLocationCheck,
		propertyId,
		selectedBuildingId,
		selectedFloorId,
		selectedRoomId
	]);

	// Clear the optimistic "awaiting analysis" flag once the subscription confirms the submission
	// has landed (evidence_submissions row exists) or the backend has moved past PENDING.
	useEffect(() => {
		if (awaitingAnalysisItemIds.size === 0) return;
		setAwaitingAnalysisItemIds(prev => {
			let changed = false;
			const next = new Set(prev);
			prev.forEach(id => {
				const item = items.find(i => i.id === id);
				if (!item) return;
				const status = (item.status as InspectionChecklistItemStatus) ?? InspectionChecklistItemStatus.PENDING;
				if (item.evidence_submissions.length > 0 || status !== InspectionChecklistItemStatus.PENDING) {
					next.delete(id);
					changed = true;
				}
			});
			return changed ? next : prev;
		});
	}, [items, awaitingAnalysisItemIds]);

	const handleResetItemEvidence = useCallback(() => {
		if (!currentItem) return;
		const itemId = currentItem.id;
		const itemName = currentItem.item_name;

		Modal.confirm({
			title: "Discard this evidence and start over?",
			content: `This will delete the uploaded file and Ollie's analysis for "${itemName}". You'll be able to capture or upload new evidence from scratch. This action can't be undone.`,
			okText: "Yes, start over",
			okButtonProps: { danger: true },
			cancelText: "Cancel",
			onOk: async () => {
				setResettingItemIds(prev => {
					const next = new Set(prev);
					next.add(itemId);
					return next;
				});

				try {
					await resetInspectionChecklistItemEvidenceMutation({
						variables: { itemId, status: InspectionChecklistItemStatus.PENDING }
					});
				} catch (error) {
					// Error already surfaced via onError handler; storage cleanup is handled in onCompleted.
				} finally {
					setResettingItemIds(prev => {
						const next = new Set(prev);
						next.delete(itemId);
						return next;
					});
				}
			}
		});
	}, [currentItem, resetInspectionChecklistItemEvidenceMutation]);

	const handleSubmitForAnalysis = useCallback(() => {
		if (!params.jobId || !canSubmitForAnalysis) return;

		setSubmittingForAnalysis(true);
		updateInspectionJobStateMutation({
			variables: {
				id: params.jobId,
				state: InspectionJobStatus.ANALYZING
			}
		});
	}, [params.jobId, canSubmitForAnalysis, updateInspectionJobStateMutation]);

	const handleSkipAndFinish = useCallback(() => {
		if (!params.jobId) return;
		const completedCount = items.filter(item => item.evidence_findings.length > 0).length;
		Modal.confirm({
			title: "Finish with incomplete evidence?",
			content: `You've completed ${completedCount} of ${items.length} items. Ollie will run gap analysis on the items with evidence and mark the rest as uninspected. Continue?`,
			okText: "Yes, finish",
			cancelText: "Keep collecting",
			onOk: () => {
				setSubmittingForAnalysis(true);
				updateInspectionJobStateMutation({
					variables: {
						id: params.jobId,
						state: InspectionJobStatus.ANALYZING
					}
				});
			}
		});
	}, [params.jobId, items, updateInspectionJobStateMutation]);

	const handleNext = useCallback(() => {
		if (currentIndex < items.length - 1) {
			setCurrentIndex(currentIndex + 1);
			return;
		}

		const hasIncompleteRequired = items.some(item => item.required && item.evidence_findings.length === 0);

		if (hasIncompleteRequired) {
			Modal.warning({
				title: "Incomplete inspection steps",
				content: "One or more inspection steps hasn't been completed. Please review and complete all steps.",
				okText: "OK"
			});
			return;
		}

		handleSubmitForAnalysis();
	}, [currentIndex, items, handleSubmitForAnalysis]);

	const handlePrevious = () => {
		if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
	};

	if (!currentItem) {
		return (
			<OllieLayout>
				<div className="iec-setup-page">
					<div className="iec-setup-bg" />
				</div>
			</OllieLayout>
		);
	}

	const baseStatus = getItemStatus(currentItem);
	const currentStatus: InspectionChecklistItemStatus = isUploading
		? InspectionChecklistItemStatus.UPLOADING
		: baseStatus;
	const currentType = getItemType(currentItem);
	const config = itemStatusConfig[currentStatus] ?? itemStatusConfig[InspectionChecklistItemStatus.PENDING];
	const StatusIcon = config.icon;

	// Analysis block data — the unified finding holds both the perception (Observations)
	// and the provisional, collection-time compliance verdict (Compliance).
	const finding = currentItem.evidence_findings[0];
	const observationPill = finding ? observationPillConfig[finding.finding_status] : undefined;
	const provisionalStatus = finding?.provisional_status ?? undefined;
	const compliancePill = provisionalStatus ? compliancePillConfig[provisionalStatus] : undefined;
	const provisionalSeverityLabel = formatSeverity(finding?.provisional_severity);
	const referenceCodes = formatApplicableCodes(currentItem.applicable_codes);
	const evidenceFiles = currentItem.evidence_submissions.filter(s => s?.file_name);
	const hasUnverifiedLocation = evidenceFiles.some(s => s?.location_verified === false);

	const renderDescription = (capture_instructions: string | string[], className: string) => {
		if (Array.isArray(capture_instructions)) {
			return (
				<ul className={className}>
					{capture_instructions.map((line, idx) => (
						<li key={idx}>{line}</li>
					))}
				</ul>
			);
		}

		// Checklist templates seeded before the rebrand still say "Iris checks:", so both
		// markers are accepted while the stored copy catches up. The rendered label is
		// always the current one.
		const checksMarker = ["Ollie checks:", "Iris checks:"].find(
			marker => typeof capture_instructions === "string" && capture_instructions.includes(marker)
		);

		if (typeof capture_instructions === "string" && checksMarker) {
			const parts = capture_instructions.split(checksMarker);
			const intro = parts[0].trim();
			const checksStr = parts[1].trim();

			// Split by (a), (b), (c) etc. using positive lookahead to keep the markers
			const checks = checksStr
				.split(/(?=\([a-z]\))/)
				.map(s => s.trim())
				.filter(Boolean);

			return (
				<div className={className}>
					{intro && <p>{intro}</p>}
					<p className="iec-instructions-label">Ollie checks:</p>
					<ul>
						{checks.map((check, idx) => {
							// Remove trailing commas from the check string if they exist
							const cleanedCheck = check.replace(/,$/, "");
							return <li key={idx}>{cleanedCheck}</li>;
						})}
					</ul>
				</div>
			);
		}

		return <p className={className}>{capture_instructions}</p>;
	};

	return (
		<OllieLayout>
			<div className="iec-setup-page">
				<div className="iec-collection-page">
					<div className="iec-collection-content">
						<Link to={routeNames.OLLIE_DASHBOARD_INSPECTIONS} className="iec-back-btn">
							<ChevronLeft className="iec-back-icon" />
							Dashboard
						</Link>

						{/* Header Island */}
						<div className="iec-header-island iec-animate">
							<div className="iec-header-property">
								<h1 className="iec-header-title">{data?.inspection_jobs_by_pk?.property.name ?? ""}</h1>
								<div className="iec-header-info-pills">
									{propertyAddress && (
										<span className="iec-header-info-pill">
											<MapPin className="iec-header-info-pill-icon" />
											{propertyAddress}
										</span>
									)}
									{data?.inspection_jobs_by_pk?.jurisdiction_record?.name && (
										<span className="iec-header-info-pill">
											<Building2 className="iec-header-info-pill-icon" />
											{data.inspection_jobs_by_pk.jurisdiction_record.name}
										</span>
									)}
									{data?.inspection_jobs_by_pk?.inspection_type && (
										<span className="iec-header-info-pill">
											<FileText className="iec-header-info-pill-icon" />
											{getInspectionTypeLabel(data.inspection_jobs_by_pk.inspection_type)}
										</span>
									)}
								</div>
							</div>
							<div className="iec-ollie-indicator">
								<div className="iec-ollie-dot-wrap">
									<div className="iec-ollie-avatar iec-ollie-avatar-sm">
										<img src={`${process.env.PUBLIC_URL}/assets/images/icons/ollie-owl.svg`} alt="Ollie AI" />
									</div>
									<span className="iec-ollie-online-ping" />
									<span className="iec-ollie-online-dot" />
								</div>
								<div>
									<p className="iec-ollie-label">Ollie AI</p>
									<p className="iec-ollie-sublabel">Evidence Collection</p>
								</div>
							</div>
						</div>

						{/* Progress & Navigator */}
						<div className="iec-progress-section iec-animate" style={{ animationDelay: "100ms" }}>
							<div className="iec-progress-header">
								<span className="iec-progress-label">
									Item {currentIndex + 1} of {items.length}
								</span>
								<span className="iec-progress-pct">{Math.round(progress)}% Complete</span>
							</div>
							<div className="iec-progress-track">
								<div className="iec-progress-bar" style={{ width: `${progress}%` }} />
							</div>

							{/* Category Stepper */}
							<div className="iec-cat-stepper">
								{Array.from(groupedItems.entries()).map(([catName, catItems]) => {
									if (catItems.length === 0) return null;

									const completed = catItems.filter(item => item.evidence_findings.length > 0).length;
									const total = catItems.length;
									const isCompleted = completed === total;
									const dashOffset = CAT_CIRCLE_C * (1 - completed / total);

									const isActive = currentItem?.category === catName;
									const firstItemIndex = items.findIndex(i => i.category === catName);

									const ci = catItems[0]?.item_category;
									const catInfo = {
										icon_source: ci?.icon_source ?? ICON_SOURCES.LUCIDE,
										icon_name: ci?.icon_name ?? "InspectionPanel",
										label: ci?.label ?? catName
									};

									return (
										<button
											key={catName}
											onClick={() => setCurrentIndex(firstItemIndex)}
											className={`iec-cat-card ${isActive ? "iec-cat-card-active" : ""} ${isCompleted && !isActive ? "iec-cat-card-complete" : ""}`}
										>
											<div className="iec-cat-card-top">
												<div
													className={`iec-cat-icon-box ${
														isActive
															? "iec-cat-icon-box-active"
															: isCompleted
																? "iec-cat-icon-box-complete"
																: "iec-cat-icon-box-default"
													}`}
												>
													<Icon source={catInfo?.icon_source as IconSource} name={catInfo?.icon_name} size={16} />
												</div>
												<div className="iec-cat-progress-wrap">
													<svg viewBox="0 0 32 32" className="iec-cat-progress-svg">
														<circle
															cx="16"
															cy="16"
															r={CAT_CIRCLE_R}
															strokeWidth="2.5"
															fill="transparent"
															className="iec-cat-progress-track"
														/>
														<circle
															cx="16"
															cy="16"
															r={CAT_CIRCLE_R}
															strokeWidth="2.5"
															fill="transparent"
															strokeDasharray={CAT_CIRCLE_C}
															strokeDashoffset={dashOffset}
															strokeLinecap="round"
															className={`iec-cat-progress-fill ${
																isCompleted
																	? "iec-cat-progress-fill-complete"
																	: isActive
																		? "iec-cat-progress-fill-active"
																		: "iec-cat-progress-fill-default"
															}`}
														/>
													</svg>
													<span
														className={`iec-cat-progress-text ${
															isCompleted
																? "iec-cat-progress-text-complete"
																: isActive
																	? "iec-cat-progress-text-active"
																	: "iec-cat-progress-text-default"
														}`}
													>
														{completed}/{total}
													</span>
												</div>
											</div>
											<div
												className={`iec-cat-label ${
													isActive
														? "iec-cat-label-active"
														: isCompleted
															? "iec-cat-label-complete"
															: "iec-cat-label-default"
												}`}
											>
												{catInfo.label}
											</div>
										</button>
									);
								})}
							</div>

							{/* Sub-steps for active category */}
							<div className="iec-substeps">
								{(groupedItems.get(currentItem?.category) || []).map((item, localIndex) => {
									const itemStatus = getItemStatus(item);
									const itemCfg =
										itemStatusConfig[itemStatus] ?? itemStatusConfig[InspectionChecklistItemStatus.PENDING];
									const ItemIcon = itemCfg.icon;
									const globalIndex = items.findIndex(i => i.id === item.id);
									const isCurrent = globalIndex === currentIndex;

									return (
										<button
											key={item.id}
											onClick={() => setCurrentIndex(globalIndex)}
											className={`iec-substep-btn ${isCurrent ? "iec-substep-btn-active" : ""}`}
										>
											<div
												className={`iec-substep-icon ${
													isCurrent
														? "iec-substep-icon-current"
														: itemStatus === InspectionChecklistItemStatus.READY
															? "iec-substep-icon-ready"
															: itemStatus === InspectionChecklistItemStatus.WARNING
																? "iec-substep-icon-warning"
																: "iec-substep-icon-default"
												}`}
											>
												<ItemIcon
													className={`iec-substep-icon-svg ${
														isCurrent ||
														[
															InspectionChecklistItemStatus.READY,
															InspectionChecklistItemStatus.WARNING,
															InspectionChecklistItemStatus.UPLOADING
														].includes(itemStatus)
															? ""
															: itemCfg.colorClass
													}`}
												/>
											</div>
											<div className="iec-substep-info">
												<span className={`iec-substep-step-num ${isCurrent ? "iec-substep-step-num-active" : ""}`}>
													Step {localIndex + 1}
												</span>
												<span
													className={`iec-substep-name ${
														isCurrent
															? "iec-substep-name-active"
															: [
																		InspectionChecklistItemStatus.READY,
																		InspectionChecklistItemStatus.WARNING,
																		InspectionChecklistItemStatus.UPLOADING
																  ].includes(itemStatus)
																? "iec-substep-name-done"
																: ""
													}`}
												>
													{item.item_name}
												</span>
											</div>
										</button>
									);
								})}
							</div>
						</div>

						{/* Submit for gap analysis — appears once all required items have a finalized analysis */}
						{canSubmitForAnalysis && (
							<div className="iec-submit-analysis-card iec-animate">
								<div className="iec-submit-analysis-info">
									<div className="iec-submit-analysis-icon">
										<Sparkles className="iec-submit-analysis-icon-svg" />
									</div>
									<div>
										<p className="iec-submit-analysis-title">All required items collected</p>
										<p className="iec-submit-analysis-sub">
											Ollie will run a full gap analysis against the applicable codes and generate your inspection
											readiness report.
										</p>
									</div>
								</div>
								<button
									className="iia-start-btn"
									onClick={handleSubmitForAnalysis}
									disabled={submittingForAnalysis || loadingUpdateInspectionJobState}
								>
									{submittingForAnalysis || loadingUpdateInspectionJobState ? (
										<>
											<Loader2 className="iia-start-btn-icon iec-spinner" />
											Submitting...
										</>
									) : (
										<>
											<Sparkles className="iia-start-btn-icon" />
											Submit for Gap Analysis
										</>
									)}
								</button>
							</div>
						)}

						{/* Main Card */}
						<div
							key={currentIndex}
							className={`iec-main-card iec-fade-in ${config.borderClass} ${isUploading ? "iec-main-card-capturing" : ""}`}
						>
							<div className="iec-main-card-body">
								{/* Status Icon */}
								<div className="iec-status-icon-wrap">
									<div className={`iec-status-icon-circle ${config.bgClass}`}>
										<StatusIcon className={`iec-status-icon ${config.colorClass}`} />
									</div>
								</div>

								{/* Category badge + Title */}
								<div className="iec-item-category-badge">
									<span>{currentItem.item_category?.label}</span>
								</div>
								<h2 className="iec-item-title">{currentItem.item_name}</h2>

								{/* Instructions: with example image or standalone */}
								{currentItem.example_image_url ? (
									<div className="iec-example-section">
										<div className="iec-example-img-wrap">
											<img
												src={currentItem.example_image_url}
												alt="Example"
												className="iec-example-img"
												onError={e => {
													(e.target as HTMLImageElement).style.display = "none";
												}}
											/>
											<div className="iec-example-badge">
												<Camera className="iec-example-badge-icon" />
												Example
											</div>
										</div>
										<div className="iec-example-instructions">
											<h4 className="iec-example-instructions-title">
												<Sparkles className="iec-example-instructions-icon" />
												Capture Instructions
											</h4>
											{renderDescription(currentItem.capture_instructions ?? "", "iec-example-instructions-text")}
										</div>
									</div>
								) : (
									<div className="iec-doc-instructions">
										<h4 className="iec-example-instructions-title">
											<Sparkles className="iec-example-instructions-icon" />
											What to upload
										</h4>
										{renderDescription(currentItem.capture_instructions ?? "", "iec-example-instructions-text")}
									</div>
								)}

								{/* Every level is optional: what is left empty widens the scope of the evidence
								    (a whole floor, a whole building, the whole property). */}
								{hasRoomHierarchy && (
									<div className="iec-room-selector">
										<h4 className="iec-example-instructions-title">
											<MapPin className="iec-example-instructions-icon" />
											Where does this evidence apply?
										</h4>
										<p className="iec-room-selector-hint">
											Optional — narrow it down as much as you can. Left empty, the evidence applies to the whole
											property.
										</p>
										<div className="iec-room-selector-row">
											<Select
												className="iec-room-select"
												popupClassName="iec-room-select-dropdown"
												popupMatchSelectWidth={false}
												allowClear
												placeholder="Building"
												value={selectedBuildingId ?? undefined}
												onChange={buildingId => {
													setSelectedBuildingId(buildingId ?? null);
													setSelectedFloorId(null);
													setSelectedRoomId(null);
												}}
												options={buildings.map(b => ({ value: b.id, label: b.name }))}
											/>
											<Select
												className="iec-room-select"
												popupClassName="iec-room-select-dropdown"
												popupMatchSelectWidth={false}
												allowClear
												placeholder="Floor"
												value={selectedFloorId ?? undefined}
												disabled={!selectedBuildingId}
												onChange={floorId => {
													setSelectedFloorId(floorId ?? null);
													setSelectedRoomId(null);
												}}
												options={floorsForBuilding.map(f => ({ value: f.id, label: f.name }))}
											/>
											<Select
												className="iec-room-select"
												popupClassName="iec-room-select-dropdown"
												popupMatchSelectWidth={false}
												allowClear
												placeholder="Room"
												value={selectedRoomId ?? undefined}
												disabled={!selectedFloorId}
												onChange={roomId => setSelectedRoomId(roomId ?? null)}
												options={roomsForFloor
													.filter(r => r.is_inspectable !== false)
													.map(r => ({ value: r.id, label: r.name }))}
											/>
										</div>
									</div>
								)}

								{/* Status: Uploading */}
								{isUploading && (
									<div className="iec-status-area iec-status-area-uploading iec-animate">
										<div className="iec-status-area-icon-wrap iec-status-area-icon-blue">
											<Loader2 className="iec-spinner" />
										</div>
										<div>
											<p className="iec-status-area-title iec-status-area-title-blue">Uploading securely...</p>
											<p className="iec-status-area-sub iec-status-area-sub-blue">
												Encrypting and transferring evidence
											</p>
										</div>
									</div>
								)}

								{/* Status: Analyzing (server-confirmed or optimistic post-submit) */}
								{!isUploading && (isAwaitingAnalysis || baseStatus === InspectionChecklistItemStatus.ANALYZING) && (
									<div className="iec-status-area iec-status-area-analyzing iec-animate">
										<div className="iec-shimmer" />
										<div className="iec-status-area-analyzing-inner">
											<div className="iec-ollie-avatar iec-ollie-avatar-sm iec-ollie-avatar-pulse">
												<img src={`${process.env.PUBLIC_URL}/assets/images/icons/ollie-owl.svg`} alt="Ollie AI" />
											</div>
											<div>
												<p className="iec-status-area-title iec-status-area-title-purple">Ollie is analyzing...</p>
												<p className="iec-status-area-sub iec-status-area-sub-purple">
													{`Checking compliance with ${currentItem.applicable_codes}`}
												</p>
											</div>
										</div>
									</div>
								)}

								{/* Analysis block — perception (Observations) + provisional compliance verdict (Compliance) */}
								{finding &&
									(baseStatus === InspectionChecklistItemStatus.READY ||
										baseStatus === InspectionChecklistItemStatus.WARNING ||
										baseStatus === InspectionChecklistItemStatus.FAIL) && (
										<>
											<div className="iec-analysis-block iec-animate">
												<div className="iec-analysis-head">
													<div className="iec-ollie-avatar iec-ollie-avatar-sm iec-ollie-avatar-flex-shrink">
														<img src={`${process.env.PUBLIC_URL}/assets/images/icons/ollie-owl.svg`} alt="Ollie AI" />
													</div>
													<div>
														<p className="iec-analysis-title">Ollie&apos;s Analysis</p>
														<p className="iec-analysis-subtitle">Automated compliance review</p>
													</div>
												</div>

												{/* Observations — what Ollie physically saw */}
												<div className="iec-an-card">
													<div className="iec-an-section-head">
														<div className="iec-an-section-label">
															<Eye className="iec-an-section-icon" />
															<span>Observations</span>
														</div>
														<div className="iec-an-meta">
															<ConfidenceMeter value={finding.confidence} />
															{observationPill && (
																<>
																	<span className="iec-an-divider" />
																	<span className={`iec-an-pill iec-an-pill-${observationPill.tone}`}>
																		<observationPill.icon className="iec-an-pill-icon" />
																		{observationPill.label}
																	</span>
																</>
															)}
														</div>
													</div>
													{finding.iris_analysis && <p className="iec-an-text">{finding.iris_analysis}</p>}
												</div>

												{/* Compliance — provisional, collection-time verdict against the criteria */}
												{compliancePill && (
													<div className="iec-an-card">
														<div className="iec-an-section-head">
															<div className="iec-an-section-label">
																<ShieldCheck className="iec-an-section-icon" />
																<span>Compliance</span>
															</div>
															<div className="iec-an-meta">
																<ConfidenceMeter value={finding.provisional_confidence} />
																<span className="iec-an-divider" />
																<span className={`iec-an-status-dot iec-an-dot-${compliancePill.tone}`} />
																<span className="iec-an-status-label">{compliancePill.label}</span>
															</div>
														</div>

														<div className="iec-an-verdict">
															<div className="iec-an-verdict-row">
																<span className={`iec-an-chip iec-an-pill-${compliancePill.tone}`}>
																	{compliancePill.label}
																</span>
																{provisionalSeverityLabel &&
																	provisionalStatus !== InspectionProvisionalVerdictStatus.PASS && (
																		<span className="iec-an-severity">• {provisionalSeverityLabel}</span>
																	)}
															</div>
															{finding.provisional_reason && (
																<p className="iec-an-text">{finding.provisional_reason}</p>
															)}
															{finding.provisional_corrective_action &&
																provisionalStatus !== InspectionProvisionalVerdictStatus.PASS && (
																	<div className="iec-an-action">
																		<AlertCircle className="iec-an-action-icon" />
																		<div>
																			<p className="iec-an-action-label">Recommended Action</p>
																			<p className="iec-an-action-text">{finding.provisional_corrective_action}</p>
																		</div>
																	</div>
																)}
														</div>

														{referenceCodes && (
															<div className="iec-an-reference">
																<span className="iec-an-reference-label">Reference Source</span>
																<span className="iec-an-reference-chip">
																	<span className="iec-an-reference-code">{referenceCodes}</span>
																	<span className="iec-an-reference-sep" />
																	<Sparkles className="iec-an-reference-ai-icon" />
																	<span className="iec-an-reference-ai">AI-Generated</span>
																</span>
															</div>
														)}

														<div className="iec-an-disclaimer">
															<Info className="iec-an-disclaimer-icon" />
															<span>Provisional results — subject to official inspector review.</span>
														</div>
													</div>
												)}
											</div>
											{/* Submitted evidence */}
											{evidenceFiles.length > 0 && (
												<div className="iec-an-evidence-container">
													<span className="iec-an-evidence-label">
														<CheckCircle2 className="iec-feedback-evidence-icon" /> Submitted Evidence (
														{evidenceFiles.length})
													</span>
													<div className="iec-analysis-block iec-animate">
														<div className="iec-an-evidence">
															<div className="iec-an-evidence-list">
																{evidenceFiles.map(submission => {
																	const isImage = submission.mime_type?.startsWith("image/");
																	const isVideo = submission.mime_type?.startsWith("video/");
																	const FileIcon = getFileIcon(submission.mime_type ?? "");

																	return (
																		<div
																			key={submission.id}
																			className="iec-feedback-evidence"
																			onClick={() => handleOpenEvidencePreview(submission)}
																		>
																			<div className="iec-feedback-evidence-thumb">
																				{submission.location_verified === false && (
																					<div className="iec-evidence-unverified-badge" title="Location not verified">
																						<AlertTriangle className="iec-evidence-unverified-badge-icon" />
																					</div>
																				)}
																				{isImage ? (
																					<img src={submission.file_url} alt={submission.file_name} />
																				) : isVideo ? (
																					<video src={submission.file_url} />
																				) : FileIcon ? (
																					<FileIcon className="iec-feedback-evidence-thumb-icon" />
																				) : null}
																			</div>
																			<div className="iec-feedback-evidence-info">
																				<p className="iec-feedback-evidence-name">{submission.file_name}</p>
																			</div>
																		</div>
																	);
																})}
															</div>
														</div>
														{hasUnverifiedLocation && (
															<div className="iec-location-unverified-notice">
																<AlertTriangle className="iec-location-unverified-icon" />
																<span>
																	Location not verified — GPS signal was insufficient to confirm this photo was captured
																	on-site.
																</span>
															</div>
														)}
													</div>
												</div>
											)}
										</>
									)}

								{/* PENDING + no staged files → dropzone */}
								{baseStatus === InspectionChecklistItemStatus.PENDING &&
									!isUploading &&
									!isAwaitingAnalysis &&
									stagedFiles.length === 0 && (
										<button
											className="iec-capture-btn"
											onClick={handleOpenFilePicker}
											onDragOver={handleDragOver}
											onDrop={handleDrop}
										>
											<div className="iec-capture-btn-dashed">
												<div className="iec-capture-btn-icon-wrap">
													{currentType === "photo" ? (
														<Camera className="iec-capture-btn-icon" />
													) : currentType === "video" ? (
														<Video className="iec-capture-btn-icon" />
													) : (
														<UploadIcon className="iec-capture-btn-icon" />
													)}
												</div>
												<div className="iec-capture-btn-text">
													<p className="iec-capture-btn-label">
														Tap to {currentType === "photo" ? "Capture" : currentType === "video" ? "Record" : "Upload"}
													</p>
													<p className="iec-capture-btn-hint">
														{currentType === "photo"
															? "Ensure good lighting"
															: currentType === "video"
																? "Keep under 60s"
																: "Select or drag and drop file"}
													</p>
												</div>
											</div>
										</button>
									)}

								{/* PENDING + staged files → file list + submit */}
								{baseStatus === InspectionChecklistItemStatus.PENDING &&
									!isUploading &&
									!isAwaitingAnalysis &&
									stagedFiles.length > 0 && (
										<>
											{stagedFiles.map(staged => {
												const isImage = staged.mimeType.startsWith("image/");
												const FileIcon = getFileIcon(staged.mimeType);
												return (
													<div
														key={staged.uid}
														className="iec-staged-preview"
														onClick={() => handleOpenPreview(staged)}
														role="button"
														tabIndex={0}
													>
														<div className="iec-staged-preview-thumb">
															{isImage ? (
																<img src={staged.localUrl} alt={staged.name} />
															) : FileIcon ? (
																<FileIcon className="iec-staged-preview-thumb-icon" />
															) : null}
														</div>
														<div className="iec-staged-preview-info">
															<p className="iec-staged-preview-name">{staged.name}</p>
															<p className="iec-staged-preview-meta">
																{staged.type} · {formatFileSize(staged.file.size)}
															</p>
														</div>
														<button
															className="iec-staged-preview-remove"
															onClick={e => {
																e.stopPropagation();
																removeStagedFile(currentItem.id, staged.uid);
															}}
															aria-label="Remove file"
														>
															<X className="iec-staged-preview-remove-icon" />
														</button>
													</div>
												);
											})}
											<button className="iec-add-more-btn" onClick={handleOpenFilePicker}>
												<UploadIcon className="iec-add-more-btn-icon" />
												Add more files
											</button>
											<button
												className="iia-start-btn"
												onClick={handleSubmitEvidence}
												disabled={isUploading || loadingSubmitEvidenceSubmissions || checkingLocation}
											>
												{checkingLocation ? (
													<>
														<MapPin className="iia-start-btn-icon iec-spinner" />
														Verifying location...
													</>
												) : loadingSubmitEvidenceSubmissions ? (
													<>
														<Loader2 className="iia-start-btn-icon iec-spinner" />
														Submitting...
													</>
												) : (
													<>
														<Sparkles className="iia-start-btn-icon" />
														Submit for Ollie Analysis
													</>
												)}
											</button>
										</>
									)}

								{/* Continue button (ready or warning) */}
								{!isUploading &&
									(baseStatus === InspectionChecklistItemStatus.READY ||
										baseStatus === InspectionChecklistItemStatus.WARNING) && (
										<button className="iec-continue-btn" onClick={handleNext}>
											{currentIndex === items.length - 1 ? (
												<>
													<Check className="iec-continue-btn-icon" />
													Finish and Submit Pre-Inspection Report
												</>
											) : (
												<>
													Continue to Next Item
													<ChevronRight className="iec-continue-btn-icon" />
												</>
											)}
										</button>
									)}

								{/* Discard & retry button (warning or fail) */}
								{!isUploading &&
									(baseStatus === InspectionChecklistItemStatus.WARNING ||
										baseStatus === InspectionChecklistItemStatus.FAIL) && (
										<button
											className="iec-continue-btn iec-continue-btn-danger"
											onClick={handleResetItemEvidence}
											disabled={isResetting || loadingResetInspectionChecklistItemEvidence}
										>
											{isResetting || loadingResetInspectionChecklistItemEvidence ? (
												<>
													<Loader2 className="iec-continue-btn-icon iec-spinner" />
													Clearing evidence...
												</>
											) : (
												<>
													<RotateCcw className="iec-continue-btn-icon" />
													Discard and start over
												</>
											)}
										</button>
									)}
							</div>

							{/* Card nav footer */}
							<div className="iec-card-nav-footer">
								<button className="iec-nav-footer-btn" onClick={handlePrevious} disabled={currentIndex === 0}>
									<ChevronLeft className="iec-nav-footer-btn-icon" />
									Previous
								</button>
								<button
									className="iec-nav-footer-btn"
									onClick={currentIndex === items.length - 1 ? handleSkipAndFinish : handleNext}
									disabled={
										isUploading || baseStatus === InspectionChecklistItemStatus.ANALYZING || submittingForAnalysis
									}
								>
									{currentIndex === items.length - 1 ? "Skip & Finish" : "Skip for now"}
									<ChevronRight className="iec-nav-footer-btn-icon" />
								</button>
							</div>
						</div>

						{/* Helper card */}
						{/* <div className="iec-helper-card iec-animate" style={{ animationDelay: "200ms" }}>
							<div className="iec-helper-icon">
								<Sparkles className="iec-helper-icon-svg" />
							</div>
							<div>
								<p className="iec-helper-title">Live AI Analysis</p>
								<p className="iec-helper-text">
									Ollie analyzes your submissions instantly. If an image is blurry or misses key details, it'll guide
									you to retake it immediately.
								</p>
							</div>
						</div> */}
					</div>
				</div>
			</div>

			{/* Unrestricted on purpose: the type is detected and stored, never used to reject an
			    upload. No `capture` either — it forces the camera on mobile and hides the browser. */}
			<input ref={fileInputRef} type="file" multiple style={{ display: "none" }} onChange={handleFileInputChange} />

			<MediaPlayer
				onCancel={handleClosePreview}
				src={fileViewer.src}
				open={!!fileViewer.src && fileViewer.type !== null}
				mediaType={fileViewer.type}
			/>

			{/* Permission denied — distinct blocking state (not the weak-signal exception). */}
			<Modal
				open={permissionDeniedOpen}
				onCancel={() => setPermissionDeniedOpen(false)}
				onOk={() => setPermissionDeniedOpen(false)}
				okText="Got it"
				cancelButtonProps={{ style: { display: "none" } }}
				title="Location permission required"
			>
				<p>
					Location access is required to submit inspection evidence, so we can confirm it was captured at the property.
				</p>
				<p>
					Please enable location for this site in your browser settings, then try again. On most phones: open the
					browser site settings (tap the lock/&#9432; icon in the address bar) → <strong>Location</strong> →{" "}
					<strong>Allow</strong>, then reload the page.
				</p>
			</Modal>
		</OllieLayout>
	);
};

export default OllieEvidenceCollection;
